import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { cn } from '@/lib/utils';
import {
  FEATURE_MATRIX,
  FINE_PRINT,
  TIER_CARDS,
  TIER_ORDER,
  normalizeTierId,
  type MatrixValue,
} from './tierCatalog';

/** The tier the pricing cards already crown "Most popular" — the matrix says
 *  so too, so the recommendation survives scrolling past the cards
 *  (Aleix, 2026-09-10). */
const RECOMMENDED_TIER = TIER_CARDS.find((t) => t.popular)?.id;

interface TierFeatureMatrixProps {
  /** Raw tier id from the backend; that column gets highlighted. */
  currentTier: string;
}

/** The ⓘ next to a feature label: hover or keyboard-focus shows the
 *  plain-language "what this means in practice" from the catalog. */
const InfoTip: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <span className="group/tip relative ml-1.5 inline-flex align-middle">
    <button
      type="button"
      aria-label={`What "${label}" means`}
      className="cursor-help rounded-full text-secondary-500 outline-none transition-[color,transform] duration-150 hover:scale-110 hover:text-secondary-600 focus-visible:scale-110 focus-visible:text-secondary-600 dark:text-secondary-400 dark:hover:text-secondary-300 dark:focus-visible:text-secondary-300"
    >
      <InformationCircleIcon className="h-[18px] w-[18px]" aria-hidden="true" />
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute left-0 top-full z-20 mt-2 w-72 rounded-xl border border-border-primary bg-surface-elevated p-3.5 text-[13px] font-normal leading-relaxed text-text-primary opacity-0 transition-opacity duration-150 group-hover/tip:opacity-100 group-focus-within/tip:opacity-100 md:bottom-full md:top-auto md:mb-2 md:mt-0"
    >
      {text}
    </span>
  </span>
);

const Cell: React.FC<{ value: MatrixValue; hook?: boolean; highlight: boolean }> = ({
  value,
  hook,
  highlight,
}) => (
  <td
    className={cn(
      'border-b border-border-primary px-2 py-2.5 text-center align-middle md:px-3.5',
      highlight && 'bg-primary-500/5 dark:bg-primary-350/10'
    )}
  >
    {value === true ? (
      <CheckIcon
        strokeWidth={3}
        className="mx-auto h-5 w-5 text-success-600 dark:text-success-400"
        aria-label="Included"
      />
    ) : value === false ? (
      <span className="text-text-disabled" aria-label="Not included">
        —
      </span>
    ) : (
      <span
        className={cn(
          'text-[13px] font-semibold leading-tight',
          hook ? 'text-secondary-600 dark:text-secondary-400' : 'text-text-primary'
        )}
      >
        {value}
      </span>
    )}
  </td>
);

/**
 * The grouped feature comparison table for the four tiers (pricing v3).
 * The workspace's current tier column is tinted.
 */
const TierFeatureMatrix: React.FC<TierFeatureMatrixProps> = ({ currentTier }) => {
  const current = normalizeTierId(currentTier);

  return (
    <div data-testid="tier-feature-matrix">
      {/* overflow-visible on md+ so the info tooltips can escape the box;
          the table only actually needs horizontal scrolling on small screens. */}
      <div className="overflow-x-auto md:overflow-visible rounded-2xl border border-border-primary bg-surface-elevated">
        {/* The wrapper's rounded border closes the table — the last row must
            not draw its own bottom border on top of it (visible since the
            wrapper stopped clipping for the tooltips). */}
        <table className="w-full border-collapse text-sm [&_tbody_tr:last-child_td]:border-b-0">
          <thead>
            <tr>
              <th className="w-[30%] border-b border-border-primary px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-text-tertiary">
                Feature
              </th>
              {TIER_ORDER.map((id) => (
                <th
                  key={id}
                  className={cn(
                    'border-b border-border-primary px-2 py-2.5 text-center text-[13px] font-bold uppercase tracking-widest md:px-3.5',
                    id === current
                      ? 'text-primary-600 dark:text-primary-350'
                      : 'text-text-secondary dark:text-primary-200'
                  )}
                >
                  {id}
                  {id === RECOMMENDED_TIER && (
                    <span className="mx-auto mt-1 block w-fit whitespace-nowrap rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider dark:bg-primary-350">
                      Recommended
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURE_MATRIX.map((group) => (
              <React.Fragment key={group.title}>
                <tr>
                  <td
                    colSpan={5}
                    className="border-b border-border-primary bg-primary-50 px-3.5 py-2 text-[13px] font-bold uppercase tracking-widest text-primary-700 dark:bg-primary-900/25 dark:text-primary-200"
                  >
                    {group.title}
                  </td>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.label}>
                    <td className="border-b border-border-primary px-3.5 py-2.5 text-left font-semibold text-text-primary">
                      {row.label}
                      <InfoTip label={row.label} text={row.info} />
                      {row.sub && (
                        <span className="mt-0.5 hidden text-xs font-normal leading-tight text-text-tertiary sm:block">
                          {row.sub}
                        </span>
                      )}
                    </td>
                    {TIER_ORDER.map((id) => (
                      <Cell
                        key={id}
                        value={row.values[id]}
                        hook={row.hook && id === 'free'}
                        highlight={id === current}
                      />
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 max-w-[74ch] text-xs leading-relaxed text-text-tertiary">
        <span className="font-semibold text-text-secondary">Good to know: </span>
        {FINE_PRINT}
      </p>
    </div>
  );
};

export default TierFeatureMatrix;
